import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';

interface Agent {
  id: string;
  name: string;
  short_description?: string;
  description?: string;
  avatar_emoji?: string;
  color?: string;
  category?: string;
  model: string;
  visibility: 'private' | 'team' | 'organization';
  is_published: boolean;
  is_featured: boolean;
  is_system: boolean;
  usage_count: number;
  avg_rating: number;
  total_ratings: number;
  personality?: {
    tone: string;
    verbosity: string;
    proactivity: string;
  };
  product_scopes?: string[];
  enabled_tools?: string[];
  created_by: string;
  created_at: string;
  updated_at: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  operations: 'Operations',
  finance: 'Finance',
  technical: 'Technical',
  'client-facing': 'Client-Facing',
  productivity: 'Productivity',
  writing: 'Writing',
  coding: 'Coding',
  analysis: 'Analysis',
};

export function Agents() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [visibilityFilter, setVisibilityFilter] = useState<string>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['agents', search, categoryFilter, visibilityFilter],
    queryFn: () => {
      const params: Record<string, string> = {};
      if (search) params['search'] = search;
      if (categoryFilter !== 'all') params['category'] = categoryFilter;
      if (visibilityFilter !== 'all') params['visibility'] = visibilityFilter;
      return api<{ custom_gpts: Agent[] }>(`custom-gpts`, { query: params });
    },
  });

  const agents = data?.custom_gpts ?? [];

  const categories = Array.from(new Set(agents.map(a => a.category).filter(Boolean)));

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-foreground mb-1">AI Agents</h1>
          <p className="text-muted-foreground">Custom THEO agents tailored to your MSP workflows.</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/agents/templates')}
            className="px-4 py-2 rounded-md text-sm border hover:bg-muted font-medium"
          >
            Browse Templates
          </button>
          <button
            onClick={() => navigate('/agents/new')}
            className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium"
          >
            + Create Agent
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search agents..."
          className="flex-1 min-w-48 border rounded-md px-3 py-2 bg-background text-sm"
        />
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="border rounded-md px-3 py-2 bg-background text-sm"
        >
          <option value="all">All Categories</option>
          {categories.map(cat => (
            <option key={cat} value={cat!}>{CATEGORY_LABELS[cat!] ?? cat}</option>
          ))}
        </select>
        <select
          value={visibilityFilter}
          onChange={(e) => setVisibilityFilter(e.target.value)}
          className="border rounded-md px-3 py-2 bg-background text-sm"
        >
          <option value="all">All Visibility</option>
          <option value="private">Private</option>
          <option value="team">Team</option>
          <option value="organization">Organization</option>
        </select>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3,4,5,6].map(i => <div key={i} className="h-48 bg-muted animate-pulse rounded-lg" />)}
        </div>
      ) : agents.length === 0 ? (
        <div className="rounded-lg border bg-card p-16 text-center">
          <div className="text-4xl mb-4">🤖</div>
          <h3 className="text-lg font-medium text-foreground mb-2">No agents yet</h3>
          <p className="text-muted-foreground mb-6">Create your first custom AI agent or start from a template.</p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => navigate('/agents/templates')}
              className="px-4 py-2 rounded-md text-sm border hover:bg-muted font-medium"
            >
              Browse Templates
            </button>
            <button
              onClick={() => navigate('/agents/new')}
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium"
            >
              Create Agent
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {agents.map((agent) => (
            <div
              key={agent.id}
              className="rounded-lg border bg-card p-5 hover:border-primary/50 transition-colors cursor-pointer group"
              onClick={() => navigate(`/agents/${agent.id}`)}
            >
              <div className="flex items-start gap-3 mb-3">
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center text-xl shrink-0"
                  style={{ backgroundColor: agent.color ? `${agent.color}20` : 'rgb(var(--primary) / 0.1)' }}
                >
                  {agent.avatar_emoji ?? '🤖'}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <h3 className="font-semibold text-foreground truncate">{agent.name}</h3>
                    {agent.is_system && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 shrink-0">System</span>
                    )}
                    {agent.is_featured && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 shrink-0">Featured</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {agent.category ? (CATEGORY_LABELS[agent.category] ?? agent.category) : agent.model}
                  </p>
                </div>
              </div>

              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                {agent.short_description ?? agent.description ?? 'No description'}
              </p>

              {/* Product scope badges */}
              {agent.product_scopes && agent.product_scopes.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {agent.product_scopes.slice(0, 4).map(scope => (
                    <span key={scope} className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase font-medium">
                      {scope}
                    </span>
                  ))}
                  {agent.product_scopes.length > 4 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                      +{agent.product_scopes.length - 4}
                    </span>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <div className="flex items-center gap-3">
                  <span>{agent.usage_count} uses</span>
                  {agent.total_ratings > 0 && (
                    <span>⭐ {agent.avg_rating.toFixed(1)}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                    agent.visibility === 'organization' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : agent.visibility === 'team' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
                  }`}>
                    {agent.visibility}
                  </span>
                  {!agent.is_system && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/agents/${agent.id}/edit`);
                      }}
                      className="opacity-0 group-hover:opacity-100 text-primary hover:text-primary/80 transition-opacity"
                    >
                      Edit
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
